import { Capacitor } from "@capacitor/core";
import axios from "axios";

// Apple In-App Purchase (iOS app only) — App Review 3.1.1 requires selling digital
// plans inside the iOS app through StoreKit; EdfaPay keeps serving web/Android.
// The purchase happens on-device, but activation is server-side only, after the
// transaction is verified via POST /api/payment/apple/verify — the client's word
// alone never activates a plan.

const PRODUCT_HOUR = process.env.REACT_APP_APPLE_PRODUCT_HOUR || "taweel_hour";
const PRODUCT_MONTHLY = process.env.REACT_APP_APPLE_PRODUCT_MONTHLY || "taweel_pro_monthly";

// StoreKit allows at most 10 units of a consumable in a single purchase
const IOS_MAX_HOURS = 10;

const isIOSApp = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

const loadPlugin = async () => {
  const mod = await import("@capgo/native-purchases");
  const PT = mod.PURCHASE_TYPE || { INAPP: "inapp", SUBS: "subs" };
  return { NativePurchases: mod.NativePurchases, PT };
};

// App Store review requirement: displayed prices must come from StoreKit itself
// (priceString), never hardcoded values.
//
// When StoreKit returns nothing the UI can only show "…", so every failure is
// logged and returned in `error`. An empty product list is NOT an exception —
// StoreKit resolves successfully with zero products when the IDs are unknown to
// App Store Connect, the Paid Applications Agreement is unsigned, or the build's
// bundle ID does not match — so that case is reported explicitly too.
const getAppleProducts = async () => {
  const out = { hour: null, monthly: null, error: null };
  let NativePurchases;
  let PT;
  try {
    ({ NativePurchases, PT } = await loadPlugin());
  } catch (e) {
    out.error = `plugin-load-failed: ${e?.message || e}`;
    console.error("[AppleIAP] native-purchases plugin failed to load — did you run `npx cap sync ios` after installing it?", e);
    return out;
  }
  const fetchOne = async (identifier, productType) => {
    try {
      const r = await NativePurchases.getProducts({ productIdentifiers: [identifier], productType });
      const product = r?.products?.[0] || null;
      if (!product) {
        console.error(`[AppleIAP] StoreKit returned no product for "${identifier}". Check: the product exists in App Store Connect with this exact ID and is "Ready to Submit", the Paid Applications Agreement is active, and the build's bundle ID matches.`);
      }
      return { product, error: product ? null : `no-product:${identifier}` };
    } catch (e) {
      console.error(`[AppleIAP] getProducts("${identifier}") threw:`, e);
      return { product: null, error: `${identifier}: ${e?.message || e}` };
    }
  };
  const [hourRes, monthlyRes] = await Promise.all([
    fetchOne(PRODUCT_HOUR, PT.INAPP),
    fetchOne(PRODUCT_MONTHLY, PT.SUBS)
  ]);
  out.hour = hourRes.product;
  out.monthly = monthlyRes.product;
  const errors = [hourRes.error, monthlyRes.error].filter(Boolean);
  if (errors.length) out.error = errors.join(" | ");
  return out;
};

// Returns the /apple/verify result: { status, planType, plan, remainingSeconds? }
// Failures are logged with their real StoreKit / server cause before being
// re-thrown, so "payment failed" in the UI is always traceable in the console.
const purchaseApplePlan = async (planType, hours = 1) => {
  const { NativePurchases, PT } = await loadPlugin();
  const isHour = planType === "hourly";
  const qty = isHour ? Math.max(1, Math.min(IOS_MAX_HOURS, Math.floor(hours) || 1)) : 1;
  const productIdentifier = isHour ? PRODUCT_HOUR : PRODUCT_MONTHLY;
  let result;
  try {
    result = await NativePurchases.purchaseProduct({
      productIdentifier,
      productType: isHour ? PT.INAPP : PT.SUBS,
      quantity: qty
    });
  } catch (e) {
    console.error(`[AppleIAP] purchaseProduct("${productIdentifier}") failed:`, e);
    throw e;
  }
  const transactionId = result?.transactionId;
  if (!transactionId) {
    console.error("[AppleIAP] purchase returned no transactionId. Raw result:", result);
    throw new Error("Purchase did not return a transaction id");
  }
  try {
    const res = await axios.post("/api/payment/apple/verify", { transactionId });
    return res.data;
  } catch (e) {
    // The money may already have been taken by Apple at this point — server-side
    // verification failing is a different (and more serious) problem than the
    // purchase itself failing, so name it distinctly.
    console.error(
      "[AppleIAP] server verification failed after a successful Apple purchase.",
      "transactionId=", transactionId,
      "status=", e?.response?.status,
      "body=", e?.response?.data
    );
    throw e;
  }
};

// Apple requires a "Restore Purchases" affordance for subscriptions — we re-verify
// every past transaction on the server; any still-active subscription re-activates.
const restoreApplePurchases = async () => {
  const { NativePurchases } = await loadPlugin();
  const r = await NativePurchases.restorePurchases();
  const txs = r?.transactions || r?.purchases || (Array.isArray(r) ? r : []);
  let lastOk = null;
  for (const tr of txs) {
    const id = tr?.transactionId;
    if (!id) continue;
    try {
      const res = await axios.post("/api/payment/apple/verify", { transactionId: id });
      if (res.data?.status === "success") lastOk = res.data;
    } catch {
    }
  }
  return lastOk;
};

export { isIOSApp, IOS_MAX_HOURS, getAppleProducts, purchaseApplePlan, restoreApplePurchases };
